import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, LogOut, RefreshCw, Plus, Edit, Save, Upload, FileSpreadsheet, Eye, EyeOff,
  Users, Crown, Zap, Lock, CreditCard, ArrowUpRight, X as XIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  getPartnerMe, getPartnerProperties, createPartnerProperty,
  updatePartnerProperty, togglePartnerPropertyVisibility,
  uploadBrochure, getAllCountries, getPartnerLeads, unlockPartnerLead,
  uploadPropertyImages, uploadLayoutPlans
} from '../services/api';
import { toast } from 'sonner';
import PropertyFormWizard from '../components/PropertyFormWizard';
import Logo from '../components/Logo';
import FeatureSelector from '../components/FeatureSelector';

const TIER_STYLES = {
  Basic: { color: 'bg-[#A1A1AA]/20 text-[#A1A1AA] border-[#A1A1AA]/30', icon: Building2 },
  Professional: { color: 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30', icon: Zap },
  Enterprise: { color: 'bg-violet-400/20 text-violet-300 border-violet-400/30', icon: Crown },
};

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadCredits, setLeadCredits] = useState(0);
  const [leadCost, setLeadCost] = useState(10);
  const [unlockingLead, setUnlockingLead] = useState(null);
  const [activeTab, setActiveTab] = useState('properties');
  const [loading, setLoading] = useState(true);
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [dynamicCountries, setDynamicCountries] = useState([]);
  const [brochureUploading, setBrochureUploading] = useState(false);
  const brochureInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [imageFiles, setImageFiles] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);

  const regions = ['Europe', 'Middle East', 'Americas', 'Asia', 'Africa', 'Oceania', 'South America', 'Caribbean'];
  const propertyTypes = ['Villa', 'Apartment', 'Penthouse', 'Townhouse', 'Estate', 'House', 'Condo', 'Flat'];
  const visaTypes = ['Golden Visa', 'Investor Visa', 'EB-5 Investor Visa', 'Start-Up Visa', 'Global Investor Programme', 'Residency Visa'];

  const [propertyForm, setPropertyForm] = useState({
    title: '', description: '', price_usd: '', country: '', region: '', city: '',
    property_type: '', bedrooms: '', bathrooms: '', area_sqft: '', image: '', images: '',
    visa_eligible: true, visa_type: 'Golden Visa', min_investment: '', features: '',
    latitude: '', longitude: '', video_url: ''
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await getPartnerMe();
        setUser(userData);
        fetchData();
      } catch {
        toast.error('Please login to access the partner portal');
        navigate('/partner');
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [propsData, countriesData, leadsData] = await Promise.all([
        getPartnerProperties(),
        getAllCountries().catch(() => ({ countries: [] })),
        getPartnerLeads().catch(() => ({ leads: [] }))
      ]);
      setProperties(propsData.properties || []);
      setDynamicCountries((countriesData.countries || []).map(c => typeof c === 'string' ? c : c.name));
      setLeads(leadsData.leads || []);
      setLeadsTotal(leadsData.total || 0);
      setLeadCredits(leadsData.credits ?? 0);
      setLeadCost(leadsData.lead_cost ?? 10);
    } catch {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('partnerUser');
    toast.success('Logged out');
    navigate('/partner');
  };

  const handleUnlockLead = async (leadId) => {
    if (leadCredits < leadCost && user?.tier !== 'Enterprise') {
      toast.error(`Insufficient credits. You need ${leadCost} credits to unlock a lead.`);
      return;
    }
    setUnlockingLead(leadId);
    try {
      const result = await unlockPartnerLead(leadId);
      if (result.unlocked && result.lead) {
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ...result.lead, unlocked: true } : l));
      }
      if (result.credits_remaining !== undefined) {
        setLeadCredits(result.credits_remaining);
        setUser(prev => prev ? { ...prev, credits: result.credits_remaining } : prev);
      }
      toast.success(result.message || 'Lead unlocked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock lead');
    } finally {
      setUnlockingLead(null);
    }
  };


  const resetPropertyForm = () => {
    setPropertyForm({
      title: '', description: '', price_usd: '', country: '', region: '', city: '',
      property_type: '', bedrooms: '', bathrooms: '', area_sqft: '', image: '', images: '',
      visa_eligible: true, visa_type: 'Golden Visa', min_investment: '', features: '',
      latitude: '', longitude: '', video_url: ''
    });
    setEditingProperty(null);
    setFormStep(1);
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    
    // Upload image files first if any
    let uploadedImageUrls = [];
    if (imageFiles.length > 0) {
      setImageUploading(true);
      try {
        const result = await uploadPropertyImages(imageFiles);
        uploadedImageUrls = result.urls || [];
      } catch {
        toast.error('Image upload failed');
        setImageUploading(false);
        return;
      }
      setImageUploading(false);
    }
    
    // Merge uploaded URLs with any manually entered URLs
    let allImages = [];
    if (uploadedImageUrls.length > 0) allImages.push(...uploadedImageUrls);
    if (propertyForm.images) allImages.push(...propertyForm.images.split(',').map(s => s.trim()).filter(Boolean));
    if (allImages.length === 0 && propertyForm.image) allImages.push(propertyForm.image);
    
    const mainImage = uploadedImageUrls[0] || propertyForm.image || allImages[0] || '';
    
    const propertyData = {
      ...propertyForm,
      price_usd: parseFloat(propertyForm.price_usd),
      bedrooms: parseInt(propertyForm.bedrooms),
      bathrooms: parseInt(propertyForm.bathrooms),
      area_sqft: parseInt(propertyForm.area_sqft),
      min_investment: parseFloat(propertyForm.min_investment) || 250000,
      image: mainImage,
      images: allImages.length > 0 ? allImages : [mainImage],
      features: propertyForm.features ? propertyForm.features.split(',').map(s => s.trim()) : [],
      latitude: propertyForm.latitude ? parseFloat(propertyForm.latitude) : null,
      longitude: propertyForm.longitude ? parseFloat(propertyForm.longitude) : null,
      video_url: propertyForm.video_url || '',
      status: 'draft'
    };
    try {
      if (editingProperty) {
        await updatePartnerProperty(editingProperty, propertyData);
        toast.success('Property updated');
      } else {
        await createPartnerProperty(propertyData);
        toast.success('Property created');
      }
      setIsPropertyModalOpen(false);
      resetPropertyForm();
      setImageFiles([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleEditProperty = (property) => {
    setEditingProperty(property.id);
    setPropertyForm({
      title: property.title, description: property.description, price_usd: property.price_usd.toString(),
      country: property.country, region: property.region, city: property.city,
      property_type: property.property_type, bedrooms: property.bedrooms.toString(),
      bathrooms: property.bathrooms.toString(), area_sqft: property.area_sqft.toString(),
      image: property.image, images: property.images?.join(', ') || property.image,
      visa_eligible: property.visa_eligible, visa_type: property.visa_type || 'Golden Visa',
      min_investment: property.min_investment?.toString() || '', features: property.features?.join(', ') || '',
      latitude: property.latitude?.toString() || '', longitude: property.longitude?.toString() || '',
      video_url: property.video_url || ''
    });
    setFormStep(1);
    setIsPropertyModalOpen(true);
  };

  const handleToggleVisibility = async (propertyId) => {
    try {
      const result = await togglePartnerPropertyVisibility(propertyId);
      toast.success(`Property ${result.visible ? 'shown' : 'hidden'} on website`);
      fetchData();
    } catch {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleBrochureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBrochureUploading(true);
    try {
      const result = await uploadBrochure(file);
      if (result.ai_extracted && Object.keys(result.ai_extracted).length > 0) {
        const ai = result.ai_extracted;
        const extractedImgs = result.extracted_images || [];
        const fullImageUrls = extractedImgs;
        
        setPropertyForm({
          title: ai.title || '', description: ai.description || '',
          price_usd: ai.price_usd?.toString() || '', country: ai.country || '',
          region: ai.region || '', city: ai.city || '',
          property_type: ai.property_type || '', bedrooms: ai.bedrooms?.toString() || '',
          bathrooms: ai.bathrooms?.toString() || '', area_sqft: ai.area_sqft?.toString() || '',
          image: fullImageUrls[0] || '', images: fullImageUrls.join(', '),
          visa_eligible: ai.visa_eligible ?? true,
          visa_type: ai.visa_type || 'Golden Visa',
          min_investment: ai.min_investment?.toString() || '',
          features: ai.features?.join(', ') || '',
          latitude: ai.latitude?.toString() || '', longitude: ai.longitude?.toString() || '',
          video_url: ''
        });
        setFormStep(1);
        setIsPropertyModalOpen(true);
        const imgMsg = fullImageUrls.length > 0 ? ` ${fullImageUrls.length} property images extracted.` : ' No high-res images found in PDF.';
        toast.success(`AI extracted property details from brochure!${imgMsg} Please review.`);
      } else {
        toast.info('Brochure uploaded but AI could not extract details. Please fill manually.');
        setIsPropertyModalOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Brochure upload failed');
    } finally {
      setBrochureUploading(false);
      if (brochureInputRef.current) brochureInputRef.current.value = '';
    }
  };

  const visibleCount = useMemo(() => properties.filter(p => p.visible !== false).length, [properties]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="partner-dashboard">
      {/* Header */}
      <header className="bg-[#1A1D24] border-b border-[#2A2F3A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Logo size="small" showText={false} />
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-white font-medium">
                    <span className="text-[#D4AF37]">Partner</span> <span className="text-white">Portal</span>
                  </h1>
                  {user.tier && TIER_STYLES[user.tier] && (() => {
                    const TierIcon = TIER_STYLES[user.tier].icon;
                    return (
                      <Badge className={`text-[10px] px-2 py-0.5 border ${TIER_STYLES[user.tier].color}`} data-testid="partner-tier-badge">
                        <TierIcon className="h-3 w-3 mr-1" />
                        {user.tier}
                      </Badge>
                    );
                  })()}
                </div>
                <p className="text-[#A1A1AA] text-xs">{user.company || user.name} &middot; {user.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={fetchData} className="text-[#A1A1AA] hover:text-white" data-testid="partner-refresh-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#A1A1AA] hover:text-white" data-testid="partner-logout-btn">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Properties', value: `${properties.length}/${user.max_listings === 999999 ? '∞' : user.max_listings}`, icon: Building2, color: '#D4AF37' },
            { label: 'Visible', value: visibleCount, icon: Eye, color: '#22c55e' },
            { label: 'Hidden', value: properties.length - visibleCount, icon: EyeOff, color: '#ef4444' },
            { label: 'Leads / Inquiries', value: leadsTotal, icon: Users, color: '#8b5cf6' },
            { label: 'Credits', value: user.credits >= 999999 ? '∞' : user.credits, icon: CreditCard, color: '#D4AF37' },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A1A1AA] text-xs">{s.label}</p>
                  <p className="text-2xl text-white font-semibold">{s.value}</p>
                </div>
                <s.icon className="h-5 w-5" style={{ color: s.color }} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1 mb-6 bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-1 w-fit">
          {[
            { id: 'properties', label: 'Properties', icon: Building2 },
            { id: 'leads', label: 'Leads', icon: Users, count: leadsTotal },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${activeTab === tab.id ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-[#A1A1AA] hover:text-white'}`}
              data-testid={`partner-tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] px-1.5 py-0.5 rounded-full font-medium">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
        {/* Upgrade Banner for Basic & Professional */}
        {(user.tier === 'Basic' || user.tier === 'Professional') && (
          <div className="mb-6 bg-gradient-to-r from-[#D4AF37]/10 to-violet-500/10 border border-[#D4AF37]/20 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-white text-sm font-medium">
                {user.tier === 'Basic' ? 'Unlock lead access & more listings' : 'Upgrade for unlimited listings & leads'}
              </p>
              <p className="text-[#A1A1AA] text-xs mt-0.5">
                {user.tier === 'Basic' ? 'Basic plan: 5 listings, no lead details. Upgrade to Professional or Enterprise.' : 'Professional plan: 25 listings, 50 credits/listing. Go Enterprise for unlimited.'}
              </p>
            </div>
            <Link to="/partner/plans">
              <Button size="sm" className="btn-luxury h-8 text-xs" data-testid="partner-upgrade-btn">
                <ArrowUpRight className="h-3 w-3 mr-1" /> View Plans & Upgrade
              </Button>
            </Link>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl text-white" data-testid="partner-properties-heading">My Properties ({properties.length})</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={brochureInputRef} type="file" accept=".pdf" className="hidden" onChange={handleBrochureUpload} data-testid="partner-brochure-input" />
            {user.tier !== 'Basic' ? (
              <Button size="sm" className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 h-8 text-xs" onClick={() => brochureInputRef.current?.click()} disabled={brochureUploading} data-testid="partner-upload-brochure-btn">
                <FileSpreadsheet className="h-3 w-3 mr-1" /> {brochureUploading ? 'Extracting...' : 'AI from PDF'}
              </Button>
            ) : (
              <Button size="sm" className="bg-[#2A2F3A]/50 text-[#A1A1AA]/50 h-8 text-xs cursor-not-allowed" disabled title="AI extraction is available on Professional and Enterprise plans" data-testid="partner-upload-brochure-btn">
                <Lock className="h-3 w-3 mr-1" /> AI from PDF
              </Button>
            )}
            <Button
              size="sm" className="btn-luxury h-8 text-xs" data-testid="partner-add-property-btn"
              disabled={user.max_listings && properties.length >= user.max_listings}
              onClick={() => {
                if (user.max_listings && properties.length >= user.max_listings) { toast.error(`Listing limit reached (${user.max_listings} for ${user.tier}). Upgrade to add more.`); return; }
                setEditingProperty(null); setIsPropertyModalOpen(true);
              }}
            >
              <Plus className="h-3 w-3 mr-1" /> Add Property {user.max_listings && user.max_listings < 999999 ? `(${properties.length}/${user.max_listings})` : ''}
            </Button>
            {isPropertyModalOpen && (
              <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="property-modal">
                <div className="fixed inset-0 bg-black/80" />
                <div className="fixed inset-0 overflow-y-auto">
                  <div className="flex min-h-full items-center justify-center p-4">
                    <div className="relative w-full max-w-3xl bg-[#1A1D24] border border-[#2A2F3A] text-white rounded-lg p-6 shadow-lg max-h-[90vh] overflow-y-auto">
                      <div className="flex flex-col space-y-1.5 text-left mb-4">
                        <h2 className="text-lg font-semibold leading-none tracking-tight">{editingProperty ? 'Edit Property' : 'Add New Listing'}</h2>
                      </div>
                      <button type="button" onClick={() => { setIsPropertyModalOpen(false); resetPropertyForm(); }} className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity z-10" data-testid="close-property-dialog">
                        <XIcon className="h-4 w-4 text-white" />
                      </button>
                      <PropertyFormWizard
                        editData={editingProperty}
                        submitting={submitting}
                        tierAllowsAI={user?.tier !== 'Basic'}
                        onSubmit={async (payload) => {
                          setSubmitting(true);
                          try {
                            if (editingProperty) {
                              await updatePartnerProperty(editingProperty.id, payload);
                              toast.success('Property updated!');
                            } else {
                              await createPartnerProperty(payload);
                              toast.success('Property created!');
                            }
                            setIsPropertyModalOpen(false);
                            resetPropertyForm();
                            const updated = await getPartnerProperties();
                            setProperties(updated);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to save');
                          } finally {
                            setSubmitting(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Properties Table */}
        <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl">
          {properties.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="h-12 w-12 text-[#2A2F3A] mx-auto mb-4" />
              <p className="text-[#A1A1AA] text-lg mb-2">No properties yet</p>
              <p className="text-[#A1A1AA] text-sm mb-4">Add your first property or upload a PDF brochure for AI extraction.</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <Table style={{ minWidth: '900px' }}>
                  <TableHeader>
                    <TableRow className="border-[#2A2F3A]">
                      <TableHead className="text-[#A1A1AA] w-16">Img</TableHead>
                      <TableHead className="text-[#A1A1AA]">Name</TableHead>
                      <TableHead className="text-[#A1A1AA]">Country</TableHead>
                      <TableHead className="text-[#A1A1AA]">Type</TableHead>
                      <TableHead className="text-[#A1A1AA]">Config</TableHead>
                      <TableHead className="text-[#A1A1AA]">Price</TableHead>
                      <TableHead className="text-[#A1A1AA]">Status</TableHead>
                      <TableHead className="text-[#A1A1AA] text-center">Visible</TableHead>
                      <TableHead className="text-[#A1A1AA]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {properties.map((prop) => (
                      <TableRow key={prop.id} className={`border-[#2A2F3A] ${!prop.visible ? 'opacity-50' : ''}`}>
                        <TableCell><img src={prop.image} alt={prop.title} className="w-14 h-10 object-cover rounded" /></TableCell>
                        <TableCell className="text-white font-medium max-w-[200px] truncate" title={prop.title}>{prop.title}</TableCell>
                        <TableCell><Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0">{prop.country}</Badge></TableCell>
                        <TableCell className="text-[#A1A1AA]">{prop.property_type}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{prop.bedrooms} BHK</TableCell>
                        <TableCell className="text-[#D4AF37] font-medium">${prop.price_usd?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={`border-0 ${prop.status === 'live' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                            {prop.status || 'draft'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch checked={prop.visible !== false} onCheckedChange={() => handleToggleVisibility(prop.id)} data-testid={`partner-toggle-${prop.id}`} />
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => handleEditProperty(prop)} className="text-[#A1A1AA] hover:text-white h-8 w-8 p-0" data-testid={`partner-edit-${prop.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
            </div>
          )}
        </div>
          </>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl">
            <div className="p-4 border-b border-[#2A2F3A] flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-lg text-white font-medium" data-testid="partner-leads-heading">
                  Property Inquiries ({leadsTotal})
                </h2>
                <p className="text-[#A1A1AA] text-xs mt-1">Unlock leads with credits to view contact details</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-[#0B0C10] border border-[#2A2F3A] rounded-lg px-3 py-1.5">
                  <CreditCard className="h-3.5 w-3.5 text-[#D4AF37]" />
                  <span className="text-white text-sm font-medium" data-testid="lead-credits-balance">{user?.tier === 'Enterprise' ? 'Unlimited' : leadCredits}</span>
                  <span className="text-[#A1A1AA] text-xs">credits</span>
                </div>
                <span className="text-[#A1A1AA] text-xs">{leadCost} per unlock</span>
              </div>
            </div>
            {leadsTotal === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 text-[#2A2F3A] mx-auto mb-4" />
                <p className="text-[#A1A1AA] text-lg mb-2">No leads yet</p>
                <p className="text-[#A1A1AA] text-sm">Leads will appear here when visitors inquire about your properties.</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <Table style={{ minWidth: '900px' }}>
                    <TableHeader>
                      <TableRow className="border-[#2A2F3A]">
                        <TableHead className="text-[#A1A1AA]">Name</TableHead>
                        <TableHead className="text-[#A1A1AA]">Email</TableHead>
                        <TableHead className="text-[#A1A1AA]">WhatsApp</TableHead>
                        <TableHead className="text-[#A1A1AA]">Property</TableHead>
                        <TableHead className="text-[#A1A1AA]">Budget</TableHead>
                        <TableHead className="text-[#A1A1AA]">Date</TableHead>
                        <TableHead className="text-[#A1A1AA] text-center">Access</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((lead) => (
                        <TableRow key={lead.id} className="border-[#2A2F3A]">
                          <TableCell className="text-white font-medium">{lead.name}</TableCell>
                          <TableCell>
                            {lead.unlocked ? (
                              <span className="text-[#A1A1AA]">{lead.work_email || lead.email}</span>
                            ) : (
                              <span className="text-[#A1A1AA]/40 flex items-center gap-1"><Lock className="h-3 w-3" /> ****@****.com</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {lead.unlocked ? (
                              <span className="text-[#A1A1AA]">{lead.whatsapp || lead.phone || '-'}</span>
                            ) : (
                              <span className="text-[#A1A1AA]/40 flex items-center gap-1"><Lock className="h-3 w-3" /> ****</span>
                            )}
                          </TableCell>
                          <TableCell className="text-[#D4AF37] max-w-[200px] truncate" title={lead.property_title || lead.preferred_destination}>
                            {lead.property_title || lead.preferred_destination || '-'}
                          </TableCell>
                          <TableCell className="text-[#A1A1AA]">{lead.investment_budget || '-'}</TableCell>
                          <TableCell className="text-[#A1A1AA] text-xs">
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {lead.unlocked ? (
                              <span className="inline-flex items-center gap-1 text-green-400 text-xs"><Eye className="h-3 w-3" /> Unlocked</span>
                            ) : (
                              <Button
                                size="sm"
                                disabled={unlockingLead === lead.id || (leadCredits < leadCost && user?.tier !== 'Enterprise')}
                                onClick={() => handleUnlockLead(lead.id)}
                                className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0B0C10] h-7 text-xs px-3 disabled:opacity-40"
                                data-testid={`unlock-lead-${lead.id}`}
                              >
                                {unlockingLead === lead.id ? (
                                  <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <Lock className="h-3 w-3 mr-1" />
                                )}
                                Unlock ({leadCost} cr)
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
              </div>
            )}
            {leadCredits === 0 && user?.tier !== 'Enterprise' && leadsTotal > 0 && (
              <div className="p-4 border-t border-[#2A2F3A] bg-[#0B0C10]/50 flex items-center justify-between">
                <p className="text-[#A1A1AA] text-sm">No credits remaining. Upgrade your plan or contact support to add more credits.</p>
                <div className="flex gap-2">
                  <Link to="/partner/plans">
                    <Button className="btn-luxury h-8 text-xs" data-testid="leads-upgrade-btn">
                      <ArrowUpRight className="h-3 w-3 mr-1" /> Upgrade Plan
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
